/**
 * La file de modération (docs/16 §2), et la garantie de docs/13 §6 à l'écran.
 *
 * ⚠️ **Le contenu ne s'affiche jamais tout seul.** Ouvrir un dossier montre ses
 * métadonnées — qui a signalé, quoi, quand, pourquoi — et un bouton. Le contenu
 * n'arrive qu'après un clic explicite, parce que **chaque consultation est
 * écrite au journal d'audit** : un écran qui le chargerait en s'affichant
 * remplirait le journal de lectures que personne n'a voulues, et le rendrait
 * illisible le jour où il faut prouver qui a lu quoi.
 *
 * La file va **du plus ancien au plus récent** — le serveur la trie ainsi — à
 * rebours de toutes les autres listes de l'application : une file de modération
 * se traite dans l'ordre d'arrivée, et ce sont les plus vieux dossiers dont la
 * fenêtre de 72 h est en train d'expirer.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useReportedContent,
  useReports,
  useResolveReport,
  type ModerationAction,
  type Report,
} from "@/features/admin/api/useAdmin";
import { Alert } from "@/shared/ui/Alert";
import { Button } from "@/shared/ui/Button";
import { Card } from "@/shared/ui/Card";
import { ErrorState } from "@/shared/ui/ErrorState";
import { Skeleton } from "@/shared/ui/Skeleton";

const ACTIONS: readonly ModerationAction[] = ["reject", "warn", "hide", "suspend"] as const;

/** Le seuil du serveur (`REASON_MIN_LENGTH`), recopié pour désactiver le bouton
 *  plutôt que d'attendre un 422. La garantie, elle, reste côté serveur. */
const NOTE_MIN_LENGTH = 10;

export function AdminReportsPage() {
  const { t, i18n } = useTranslation("admin");
  const [openOnly, setOpenOnly] = useState(true);
  const [selected, setSelected] = useState<Report | null>(null);
  const [disclosing, setDisclosing] = useState<string | null>(null);
  const [action, setAction] = useState<ModerationAction>("reject");
  const [note, setNote] = useState("");

  const queue = useReports(openOnly);
  const content = useReportedContent(disclosing);
  const resolve = useResolveReport();

  const formatDateTime = (value: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(
      new Date(value),
    );

  const select = (report: Report) => {
    setSelected(report);
    // La divulgation ne suit pas la sélection : changer de dossier referme le
    // contenu, et le rouvrir demande un second geste — donc une seconde entrée
    // au journal, ce qui est exactement ce qu'on veut compter.
    setDisclosing(null);
    setNote("");
    setAction("reject");
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t("reports.heading")}</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {t("reports.description")}
        </p>
      </div>

      <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
        <input
          type="checkbox"
          checked={openOnly}
          onChange={(event) => {
            setOpenOnly(event.target.checked);
            setSelected(null);
            setDisclosing(null);
          }}
        />
        {t("reports.openOnly")}
      </label>

      {queue.isPending ? <Skeleton className="h-40 w-full" label={t("reports.heading")} /> : null}
      {queue.isError ? <ErrorState error={queue.error} /> : null}

      {queue.data !== undefined ? (
        <div className="grid gap-6 lg:grid-cols-2">
          <Card className="overflow-hidden p-0">
            {queue.data.items.length === 0 ? (
              <p className="p-6 text-sm text-slate-500 dark:text-slate-400">{t("reports.empty")}</p>
            ) : (
              <ul className="divide-y divide-slate-200 dark:divide-slate-800">
                {queue.data.items.map((report) => (
                  <li key={report.id}>
                    <button
                      type="button"
                      onClick={() => {
                        select(report);
                      }}
                      className="w-full px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-900"
                      data-testid="report-row"
                      data-selected={selected?.id === report.id}
                    >
                      <span className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium">
                          {t(`reports.reasons.${report.reason}`, {
                            defaultValue: report.reason,
                          })}
                        </span>
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          {formatDateTime(report.created_at)}
                        </span>
                      </span>
                      <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">
                        {t(`reports.entities.${report.entity_type}`, {
                          defaultValue: report.entity_type,
                        })}
                        {report.open_reports_on_entity > 1
                          ? ` · ${t("reports.alsoReported", {
                              countValue: report.open_reports_on_entity,
                            })}`
                          : ""}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {selected !== null ? (
            <Card className="space-y-4 p-4" data-testid="report-detail">
              <div>
                <h2 className="text-lg font-semibold">
                  {t(`reports.reasons.${selected.reason}`, { defaultValue: selected.reason })}
                </h2>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  {selected.details ?? t("reports.noDetails")}
                </p>
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  {t("reports.status", {
                    statusValue: t(`reports.statuses.${selected.status}`, {
                      defaultValue: selected.status,
                    }),
                  })}
                </p>
              </div>

              {/* Le contenu, et seulement sur demande. La phrase dit pourquoi :
                  l'utilisateur du back-office doit savoir que son clic est
                  consigné, sans quoi la trace serait une surveillance cachée
                  plutôt qu'une garantie. */}
              {content.data === undefined ? (
                <div className="space-y-2">
                  <Button
                    variant="secondary"
                    disabled={!selected.can_disclose || content.isFetching}
                    onClick={() => {
                      setDisclosing(selected.id);
                    }}
                    data-testid="disclose"
                  >
                    {t("reports.disclose")}
                  </Button>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {selected.can_disclose ? t("reports.discloseAudited") : t("reports.windowGone")}
                  </p>
                </div>
              ) : (
                <ul className="space-y-2" data-testid="disclosed-content">
                  {content.data.items.map((item) => (
                    <li
                      key={item.id}
                      className={`rounded-md px-3 py-2 text-sm whitespace-pre-wrap ${
                        item.is_reported
                          ? "bg-amber-50 ring-1 ring-amber-300 dark:bg-amber-950"
                          : "bg-slate-50 dark:bg-slate-900"
                      }`}
                      data-testid="disclosed-item"
                      data-reported={item.is_reported}
                    >
                      {item.body}
                      {item.is_deleted ? (
                        <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">
                          {t("reports.itemDeleted")}
                        </span>
                      ) : null}
                      {item.is_hidden ? (
                        <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">
                          {t("reports.itemHidden")}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
              {content.isError ? <ErrorState error={content.error} /> : null}

              {selected.is_open ? (
                <form
                  className="space-y-3 border-t border-slate-200 pt-4 dark:border-slate-800"
                  onSubmit={(event) => {
                    event.preventDefault();
                    resolve.mutate(
                      { reportId: selected.id, action, note },
                      {
                        onSuccess: (updated) => {
                          setSelected(updated);
                          setDisclosing(null);
                          setNote("");
                        },
                      },
                    );
                  }}
                >
                  <fieldset className="flex flex-wrap gap-3">
                    <legend className="text-sm font-medium">{t("reports.decision")}</legend>
                    {ACTIONS.map((value) => (
                      <label key={value} className="flex items-center gap-1 text-sm">
                        <input
                          type="radio"
                          name="action"
                          value={value}
                          checked={action === value}
                          onChange={() => {
                            setAction(value);
                          }}
                        />
                        {t(`reports.actions.${value}`)}
                      </label>
                    ))}
                  </fieldset>
                  <textarea
                    value={note}
                    onChange={(event) => {
                      setNote(event.target.value);
                    }}
                    rows={3}
                    aria-label={t("reports.note")}
                    placeholder={t("reports.notePlaceholder")}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900"
                  />
                  <Button
                    type="submit"
                    disabled={note.trim().length < NOTE_MIN_LENGTH || resolve.isPending}
                  >
                    {t("reports.submit")}
                  </Button>
                  {resolve.isError ? <ErrorState error={resolve.error} /> : null}
                </form>
              ) : (
                <Alert tone="info">
                  {t("reports.alreadyHandled", { noteValue: selected.resolution ?? "" })}
                </Alert>
              )}
            </Card>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
